// ScrapeHealthModal.jsx
// READ-ONLY scrape health monitor. Queries each market's scrape collection,
// groups runs by slot (the `market` field), and shows when each slot last
// captured anything. Never writes/updates/deletes any Firestore data.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// Market display label -> root collection. Mirrors MARKET_ROOT_COLLECTION
// in PropertyGuysDashboard.jsx.
const MARKETS = [
  { label: 'Memphis', col: 'newscrapes' },
  { label: 'Northern Virginia', col: 'newscrapesVA' },
  { label: 'Houston', col: 'newscrapesHouston' },
  { label: 'Nashville', col: 'newscrapesNashville' },
  { label: 'Long Island', col: 'newscrapesLongIsland' },
  { label: 'Tampa', col: 'newscrapesTampa' },
  { label: 'South FL', col: 'newscrapesSouthFL' },
  { label: 'Ft Myers', col: 'newscrapesFtMyers' },
  { label: 'Miami', col: 'newscrapesMiami' },
  { label: 'Dallas', col: 'newscrapesDallas' },
  { label: 'Atlanta', col: 'newscrapesAtlanta' },
  { label: 'Denver', col: 'newscrapesDenver' },
  { label: 'Orlando', col: 'newscrapesOrlando' },
  { label: 'Panhandle', col: 'newscrapesPanhandle' },
  { label: 'Connecticut', col: 'newscrapesConnecticut' },
  { label: 'Raleigh', col: 'newscrapesRaleigh' },
  { label: 'Baltimore', col: 'newscrapesBaltimore' },
  { label: 'Los Angeles', col: 'newscrapesLosAngeles' },
];

// How many recent runs to scan per collection. Slots run ~hourly, so 500 docs
// gives well over 24h of history even for the busiest markets.
const SCAN_LIMIT = 500;
const HOUR = 3600 * 1000;
const ACTIVE_WINDOW_MS = 72 * HOUR; // markets w/ no run in 72h are treated as paused
const FRESH_MS = 6 * HOUR; // green
const STALE_MS = 24 * HOUR; // yellow up to here, red beyond

// Slots to hide from the monitor entirely (retired / unused). Matched against
// the `market` field, case/space-insensitive.
const EXCLUDED_SLOTS = new Set(['long island 7', 'long island 9']);
const normSlot = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const isExcludedSlot = (s) => EXCLUDED_SLOTS.has(normSlot(s));

const COLORS = {
  green: { bg: 'rgba(34,197,94,0.14)', border: '#22c55e', text: '#86efac' },
  yellow: { bg: 'rgba(234,179,8,0.16)', border: '#eab308', text: '#fde68a' },
  red: { bg: 'rgba(239,68,68,0.18)', border: '#ef4444', text: '#fca5a5' },
};

function toMillis(v) {
  if (v == null) return null;
  try {
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'number') return v;
  } catch {
    return null;
  }
  return null;
}

function relTime(ms) {
  if (ms == null) return 'never';
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function classify(lastCaptureMs) {
  if (lastCaptureMs == null) return 'red';
  const age = Date.now() - lastCaptureMs;
  if (age <= FRESH_MS) return 'green';
  if (age <= STALE_MS) return 'yellow';
  return 'red';
}

// "Dallas 7" -> { base: "Dallas", num: 7 }; "Long Island" -> { base, num: 0 }
function slotSortKey(label) {
  const m = String(label).match(/(\d+)\s*$/);
  return {
    base: String(label).replace(/\s*\d+\s*$/, '').trim(),
    num: m ? parseInt(m[1], 10) : 0,
  };
}

// Short token for the cell: strip the market label prefix if present.
function shortSlot(slot, marketLabel) {
  const s = String(slot || '').trim();
  if (marketLabel && s.toLowerCase().startsWith(marketLabel.toLowerCase())) {
    const rest = s.slice(marketLabel.length).trim();
    return rest || 'base';
  }
  const m = s.match(/(\d+)\s*$/);
  return m ? m[1] : s;
}

function buildMarketHealth(label, docs) {
  // group by raw `market` field (the slot)
  const slots = new Map();
  let marketLastRun = null;

  docs.forEach((d) => {
    const slot = d.market || '(unlabeled)';
    if (isExcludedSlot(slot)) return;
    const startMs = toMillis(d.startedAt);
    if (startMs != null && (marketLastRun == null || startMs > marketLastRun)) {
      marketLastRun = startMs;
    }
    if (!slots.has(slot)) {
      slots.set(slot, {
        slot,
        lastRunAt: null,
        lastCaptureAt: null,
        lastNewLeadAt: null,
        runs: 0,
      });
    }
    const s = slots.get(slot);
    s.runs += 1;
    if (startMs != null) {
      if (s.lastRunAt == null || startMs > s.lastRunAt) s.lastRunAt = startMs;
      const captured = typeof d.totalCaptured === 'number' && d.totalCaptured > 0;
      if (captured && (s.lastCaptureAt == null || startMs > s.lastCaptureAt)) {
        s.lastCaptureAt = startMs;
      }
      const kept = d.keptAfterDedup ?? d.keptInitial;
      const newLead = typeof kept === 'number' && kept > 0;
      if (newLead && (s.lastNewLeadAt == null || startMs > s.lastNewLeadAt)) {
        s.lastNewLeadAt = startMs;
      }
    }
  });

  const slotList = Array.from(slots.values())
    .map((s) => ({ ...s, color: classify(s.lastCaptureAt) }))
    .sort((a, b) => {
      const ka = slotSortKey(a.slot);
      const kb = slotSortKey(b.slot);
      if (ka.base !== kb.base) return ka.base.localeCompare(kb.base);
      return ka.num - kb.num;
    });

  const redCount = slotList.filter((s) => s.color === 'red').length;
  const active =
    marketLastRun != null && Date.now() - marketLastRun <= ACTIVE_WINDOW_MS;

  return { label, slots: slotList, redCount, marketLastRun, active };
}

function SlotCell({ s, marketLabel }) {
  const c = COLORS[s.color];
  const capturedNever = s.lastCaptureAt == null;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ fontSize: 12 }}>
          <div><b>{s.slot}</b></div>
          <div>Last run: {relTime(s.lastRunAt)}</div>
          <div>Last captured: {relTime(s.lastCaptureAt)}</div>
          <div>Last new lead: {relTime(s.lastNewLeadAt)}</div>
          <div>Runs scanned: {s.runs}</div>
        </Box>
      }
    >
      <Box
        sx={{
          width: 116,
          p: 1.25,
          borderRadius: 2,
          bgcolor: c.bg,
          border: `1px solid ${c.border}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#f9fafb' }}>
            {shortSlot(s.slot, marketLabel)}
          </Typography>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: c.border }} />
        </Box>
        <Typography sx={{ fontSize: 12, color: c.text, fontWeight: 700 }}>
          {capturedNever ? 'no captures' : relTime(s.lastCaptureAt)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
          {s.lastNewLeadAt ? `lead ${relTime(s.lastNewLeadAt)}` : 'no new leads'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function ScrapeHealthModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [markets, setMarkets] = useState([]); // built health per market
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.all(
        MARKETS.map(async ({ label, col }) => {
          try {
            const snap = await getDocs(
              query(collection(db, col), orderBy('startedAt', 'desc'), limit(SCAN_LIMIT))
            );
            const docs = snap.docs.map((d) => d.data());
            return buildMarketHealth(label, docs);
          } catch (e) {
            return { label, slots: [], redCount: 0, marketLastRun: null, active: false, err: e?.message };
          }
        })
      );
      setMarkets(results);
      setUpdatedAt(Date.now());
    } catch (e) {
      setError(e?.message || 'Failed to load scrape health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const active = markets.filter((m) => m.active);
  const inactive = markets.filter((m) => !m.active);
  active.sort((a, b) => b.redCount - a.redCount || a.label.localeCompare(b.label));
  const totalRed = active.reduce((sum, m) => sum + m.redCount, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0b1220',
          color: '#e5e7eb',
          border: '1px solid rgba(148,163,184,0.25)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 800,
          color: '#f9fafb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <span>Scrape Health</span>
          {!loading && (
            <Typography sx={{ fontSize: 13, color: totalRed > 0 ? '#fca5a5' : '#86efac' }}>
              {totalRed > 0 ? `${totalRed} slot${totalRed === 1 ? '' : 's'} need attention` : 'all clear'}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
            {updatedAt ? `updated ${relTime(updatedAt)}` : ''}
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={{ textTransform: 'none', color: '#e5e7eb', borderRadius: 9999 }}
          >
            Refresh
          </Button>
          <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {[
            ['green', '< 6h'],
            ['yellow', '6–24h'],
            ['red', '> 24h / dead'],
          ].map(([k, lbl]) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[k].border }} />
              <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>{lbl}</Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            color = time since the slot last captured anything (dead cookies → 0 captured → red)
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={22} sx={{ color: '#22c55e' }} />
            <Typography sx={{ color: '#94a3b8' }}>Scanning recent runs…</Typography>
          </Box>
        )}

        {error && !loading && (
          <Typography sx={{ color: '#fca5a5', py: 2 }}>{error}</Typography>
        )}

        {!loading && !error &&
          active.map((m) => (
            <Box key={m.label} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#f9fafb' }}>
                  {m.label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                  {m.slots.length} slot{m.slots.length === 1 ? '' : 's'}
                  {m.redCount > 0 ? ` · ${m.redCount} red` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {m.slots.map((s) => (
                  <SlotCell key={s.slot} s={s} marketLabel={m.label} />
                ))}
              </Box>
            </Box>
          ))}

        {!loading && !error && inactive.length > 0 && (
          <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid rgba(148,163,184,0.18)' }}>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              Paused / no runs in 72h: {inactive.map((m) => m.label).join(', ')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
